import { useParams } from 'react-router-dom'
import React from 'react'
import { useAssignments } from '../../hooks/useAssignment'
import { useProjectById } from '../../hooks/useProjectById'
import { Collapse, CollapseProps } from 'antd'
import { QuestionsTypes, QuestionType } from '../../Types'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getPrioritisedLanguage } from '../../util/languages'
import { DisconnectOutlined } from '@ant-design/icons'
import { orderBy } from 'lodash'

export const SubjectAssignment = ({ subjectLanguageIsoCode }: { subjectLanguageIsoCode: string }) => {
  const { projectId, subjectId, assignmentId } = useParams() as {
    projectId: string
    subjectId: string
    assignmentId: string
  }

  const { data: projectData } = useProjectById(projectId)
  const { data } = useAssignments(projectId, assignmentId, subjectId)

  // If one of the prioritised languages is present in the project, use that
  // - It will be checked on question level if a translation for this language exist
  const prioritisedLanguageIsoCode = getPrioritisedLanguage(projectData.languages)

  const questions = data.questions

  return (
    <div className="container-fluid">
      <div className="row" style={{}}>
        {questions.map((question, index) => {
          // Language key. We need to check on question level if a translation exists
          // - if the question does not contain a translation for the default language in the title, it is not present
          // - use the subject's language
          const languageIsoCode =
            prioritisedLanguageIsoCode && question.title[prioritisedLanguageIsoCode]
              ? prioritisedLanguageIsoCode
              : subjectLanguageIsoCode.toLowerCase()

          const title = question.title[languageIsoCode]
          const body = question.body[languageIsoCode]

          // Filter answers based on questionId
          // Sort answers by deviceTime (1. priority), createdAt (2. priority)
          const filteredAnswers = data.answers.filter((answer) => answer.questionId === question._id)
          const answers = orderBy(filteredAnswers, ['deviceTime', 'createdAt'], ['desc', 'desc'])

          if (data.answers === 'BLINDED') {
            return (
              <div key={index} className="col-12 mb-4 border-bottom pb-4">
                <h5>BLINEDE</h5>
              </div>
            )
          }

          return (
            <div key={index} className="col-12 mb-4 border-bottom pb-4">
              <h5>{title}</h5>
              <p>{body}</p>
              <AnswersFactory question={question} answers={answers} defaultLang={languageIsoCode} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const AnswersFactory = ({ question, answers, defaultLang }: { question: any; answers: any[]; defaultLang: string }) => {
  const type: QuestionType = question.type
  const showAnswers = question?.type !== QuestionsTypes.info

  const formatDate = (dateString: string) => {
    dayjs.extend(utc)

    const created = dayjs.utc(dateString).format('DD MMMM YYYY, HH:mm:ss') + ' UTC'

    return `${created}`
  }

  /**
   * Generates a label/headline for an answer. Includes
   * - createAt
   * - device time
   * - offline state
   *
   * Re. usage of span tags
   * For some answers the label is used a label in a 'Collapse' component which wraps the label in a span-tag.
   * So the only way of create a structured layout is to use span-tags
   *
   */
  const createLabel = (createdAt: string, deviceTime: string | null, isOffline: boolean, i: number = 0) => {
    const label = isOffline ? 'Sync Time: ' : i === 0 ? 'Submitted: ' : 'Answer: '
    return (
      <>
        <span style={{ display: 'block', marginBottom: !deviceTime && !isOffline ? '3px' : 'none' }}>
          {label + formatDate(createdAt)}
        </span>

        {deviceTime && (
          <span className="p-mini opacity-50" style={{ display: 'block', marginBottom: !isOffline ? '3px' : 'none' }}>
            Device time: {deviceTime}
          </span>
        )}

        {isOffline && (
          <span className="p-mini opacity-50" style={{ display: 'block', marginBottom: '3px' }}>
            Offline <DisconnectOutlined />
          </span>
        )}
      </>
    )
  }

  /**
   For each question type:
   1. Create answers
   2. Wrap answers
   */
  const createAnswer = (type: QuestionType, answer: any) => {
    const hasValue = !!answer?.value
    const answerIndex = hasValue ? parseInt(answer.value) : null

    if (answer.value === 'BLINDED') {
      return (
        <p className="d-inline-block px-2 p-small rounded-3" style={{ background: '#e6e6e6' }}>
          Value blinded
        </p>
      )
    }

    switch (type) {
      case QuestionsTypes.radio:
        // Create and wrap options
        return (
          <div>
            {question?.options.map((option, questionIndex) => {
              return (
                <OptionWrapper
                  key={`${answerIndex}-${questionIndex}`}
                  isSelected={answerIndex === questionIndex}
                  body={option[defaultLang]}
                  className="p-2 me-2 border-1 border mb-1"
                />
              )
            })}
          </div>
        )
      case QuestionsTypes.nprs11pt2l:
        // Create and wrap options
        return (
          <div className="d-flex justify-md-content-between justify-xml-content-start">
            {question?.options.intensity.map((option, questionIndex) => {
              return (
                <OptionWrapper
                  key={`${answerIndex}-${questionIndex}`}
                  isSelected={answerIndex === questionIndex}
                  body={option[defaultLang]}
                />
              )
            })}
          </div>
        )
      default:
        return hasValue ? (
          <div className="d-flex justify-md-content-between justify-xml-content-start">Answer: {answer?.value}</div>
        ) : null
    }
  }

  if (showAnswers) {
    /*
    If there is ONE answer, options are wrapped in a <div>
    If there is MORE than one answer, options are wrapped in a <Collapse> creating an accordion
   */
    if (answers.length === 1) {
      // Answer
      const answer = answers[0]
      const answerElement = createAnswer(type, answer)

      // Label
      // Backwards compatibility. answer.offline might be undefined and createLabel() expects a boolean
      const label = createLabel(answer.createdAt, answer.deviceTime ? answer.deviceTime : null, !!answer.offline)
      return (
        <div className="mt-3">
          <div className="mb-2" style={{ fontSize: '0.8rem' }}>
            {label}
          </div>
          {answerElement}
        </div>
      )
    } else if (answers.length > 1) {
      // Multiple answers
      const items: CollapseProps['items'] = answers.map((answer, i) => {
        // Answer
        const answerElement = createAnswer(type, answer)

        // Label
        // Backwards compatibility. answer.offline might be undefined and createLabel() expects a boolean
        const label = createLabel(answer.createdAt, answer.deviceTime ? answer.deviceTime : null, !!answer.offline, i)
        return {
          key: (i + 1).toString(),
          label: label, // Backwards compatibility. answer.offline might be undefined and createLabel() expects a boolean
          children: <div style={{ marginTop: '-15px' }}>{answerElement}</div>, // The negative top margin decreases distance between children and header label in collapse. Making layout a little more "tight"
          style: { borderBottom: '1px dotted #DDD' },
        }
      })

      return (
        <Collapse
          ghost
          accordion
          expandIconPlacement={'end'}
          items={items}
          defaultActiveKey={['1']}
          size="small"
          style={{ fontSize: '0.8rem' }}
        />
      )
    }

    // If no answers to display, the question has been accessed anyway. Display the date for each "answer"
  } else {
    const elements = answers.map((answer, i) => {
      // Backwards compatibility. answer.offline might be undefined and createLabel() expects a boolean
      const label = createLabel(answer.createdAt, answer.deviceTime ? answer.deviceTime : null, !!answer.offline, i)
      return (
        <div key={`element-${i}`} className="">
          <div className="" style={{ fontSize: '0.8rem' }}>
            {label}
          </div>
        </div>
      )
    })
    return <div className="mt-3">{elements}</div>
  }

  return null
}

const OptionWrapper = ({ isSelected, body, className }: { isSelected: boolean; body: string; className?: string }) => {
  return (
    <div
      className={className || 'p-2 me-2 border-1 border'}
      style={{
        color: isSelected ? 'white' : 'black',
        background: isSelected ? '#6e6e6e' : 'transparent',
      }}
    >
      <p>{body}</p>
    </div>
  )
}
